// Call detail "What happened": who ended the call, the diagnostic timeline and per-leg media
// health, each with its provenance. Server data only; nothing here is inferred by the browser.
import type { Call } from '@bcio-voice/contracts';
import { type KeyboardEvent, type MouseEvent, useRef, useState } from 'react';
import type { LucideIcon } from 'lucide-react';
import {
  AudioLines, Building2, CircleHelp, Cpu, Hourglass, Laptop, MousePointerClick, Network, NotebookPen, PhoneOff, Server, ServerCog, Settings2, UserRound, WifiOff,
} from 'lucide-react';
import type { CallNote } from '../../lib/api.ts';
import { BlocklistInlineStyles } from '../blocklist/InlineStyles.tsx';
import { Icon } from '../../ui/Icon.tsx';
import { SkeletonRows, StatePanel } from '../../ui/StatePanel.tsx';
import { useWorkspace } from '../../state/workspace.tsx';
import { useSensitiveResource } from '../../state/useSensitiveResource.ts';
import { hasPermission } from '../../lib/capabilities.ts';
import { RoutingDecisionsSection } from '../routing/RoutingDecisions.tsx';
import { formatDuration } from '../../lib/format.ts';
import {
  type CallTermination, type CallTimeline, CONFIDENCE_HINT, CONFIDENCE_LABEL, type EventSource, HEALTH_LABEL, type LegHealth, type NoteAction,
  PROVENANCE_NOTE, SOURCE_LABEL, type TagIcon, type TimelineEvent, type TimelineView, actorFor, attributedActor, callEnded, clockTime, deviceOwner, dropEventDetail,
  eventSentence, formatLoss, formatMs, hasAttribution, mediaHealth, moveFocus, noteAction, stateWord, terminationHeadline, terminationOf, terminationSentence,
  terminationTag, timelineView, timelineWindow,
} from './timeline.ts';

/** Permissions the timeline route accepts (calls:read_own or calls:read_all). */
export const TIMELINE_PERMISSIONS = ['calls:read_own', 'calls:read_all'] as const;

export const SOURCE_ICON: Record<EventSource, LucideIcon> = {
  agent_action: MousePointerClick,
  client_report: Laptop,
  controller: Server,
  pbx: ServerCog,
  carrier: Building2,
  system: Cpu,
};

export const TAG_ICON: Record<TagIcon, LucideIcon> = {
  agent: UserRound,
  remote: PhoneOff,
  network: WifiOff,
  carrier: Network,
  media: AudioLines,
  limit: Hourglass,
  system: Settings2,
  unknown: CircleHelp,
};

/**
 * `refreshToken` reloads the timeline when something else on the page added to it (a saved note).
 * `notes`, when the page has loaded them, lets note events quote the note they're about.
 */
export function WhatHappenedSection({ call, refreshToken, notes }: { call: Call; refreshToken?: string | number; notes?: readonly CallNote[] | null }) {
  const ws = useWorkspace();
  const canRead = TIMELINE_PERMISSIONS.some(p => hasPermission(ws.permissions, p));
  const t = terminationOf(call);
  const refreshKey = `${call.state}|${t.termination_side ?? ''}|${t.termination_kind ?? ''}|${t.termination_confidence ?? ''}|${refreshToken ?? ''}`;
  const resource = useSensitiveResource<{ data?: unknown }>(canRead ? `/calls/${encodeURIComponent(call.id)}/timeline` : null, refreshKey);
  const view = timelineView({ canRead, status: resource.status, value: resource.value, error: resource.error });
  return (
    <section className="detail-section what-happened" aria-labelledby="wh-title">
      <h3 id="wh-title" className="section-title">What happened</h3>
      <WhatHappenedBody view={view} call={call} onRetry={resource.reload} notes={notes ?? null} />
      {call.direction === 'inbound' ? <RoutingDecisionsSection call={call} /> : null}
    </section>
  );
}

/** The panel body for one decided state (exported for tests). */
export function WhatHappenedBody({ view, call, onRetry, notes = null }: { view: TimelineView; call: Call; onRetry?: () => void; notes?: readonly CallNote[] | null }) {
  switch (view.state) {
    case 'denied':
      return <StatePanel variant="denied" compact headingLevel={3} title="Your role can’t view call diagnostics" body="Ask an admin if you need to see who ended calls and network problems." details={{ permission: 'calls:read_own', code: view.code, requestId: view.requestId }} />;
    case 'not_found':
      return <StatePanel variant="unavailable" compact headingLevel={3} title="Diagnostics for this call aren’t available to you" body="The call may have been removed or isn’t shared with you." details={{ code: 'not_found', requestId: view.requestId }} />;
    case 'loading':
      return <SkeletonRows rows={3} label="Loading call diagnostics" />;
    case 'access':
      return view.access === 'refreshing'
        ? <p className="field-hint" role="status">Your access changed. Checking what you can see…</p>
        : <p className="inline-reason" role="status">Sign in again to see call diagnostics.</p>;
    case 'error':
      return (
        <StatePanel
          variant={view.error.code === 'network_error' ? 'offline' : 'error'}
          compact
          headingLevel={3}
          title={view.error.code === 'network_error' ? view.error.title : 'Couldn’t load call diagnostics'}
          body={view.error.code === 'network_error' ? undefined : 'Your call history isn’t affected.'}
          action={onRetry ? <button type="button" className="btn" onClick={onRetry}>Try again</button> : undefined}
          details={{ code: view.error.code, requestId: view.error.requestId }}
        />
      );
    case 'empty':
      // No events: "pending" would promise attribution that may never come, so only a known end is shown.
      return (
        <>
          {hasAttribution(terminationOf(call)) ? <Summary call={call} termination={terminationOf(call)} events={[]} /> : null}
          <StatePanel variant="empty" compact headingLevel={3} title="No diagnostics were recorded for this call" body="Calls from before diagnostics were turned on, and calls that never reached the phone system, have none." />
        </>
      );
    case 'ready':
      return <Ready call={call} timeline={view.timeline} notes={notes} />;
  }
}

function Ready({ call, timeline, notes }: { call: Call; timeline: CallTimeline; notes: readonly CallNote[] | null }) {
  // The timeline response is newer than the cached call when they differ; prefer its attribution.
  const termination = hasAttribution(timeline.call) ? timeline.call : terminationOf(call);
  return (
    <>
      <Summary call={call} termination={termination} events={timeline.events} />
      {timeline.events.length === 0 ? (
        <p className="field-hint">No events were recorded for this call.</p>
      ) : (
        <>
          <h4 className="subsection-title" id="wh-events">Events</h4>
          <EventList call={call} events={timeline.events} notes={notes} />
        </>
      )}
      {timeline.skipped > 0 ? <p className="meta">{timeline.skipped} event{timeline.skipped === 1 ? '' : 's'} couldn’t be shown in this version.</p> : null}
      <MediaHealth call={call} legs={mediaHealth(timeline.events)} />
    </>
  );
}

function Summary({ call, termination, events }: { call: Call; termination: CallTermination; events: readonly TimelineEvent[] }) {
  const ws = useWorkspace();
  if (!hasAttribution(termination)) {
    const ended = callEnded(call);
    return (
      <div className="wh-summary">
        <p className="wh-headline">
          <Icon icon={Hourglass} /> {ended ? 'Attribution pending' : 'This call hasn’t ended'}
        </p>
        <p className="meta">{ended ? 'The server hasn’t worked out who ended this call yet. Recorded events are listed below.' : 'Who ended the call is shown here once it ends.'}</p>
        <p className="meta wh-provenance">{PROVENANCE_NOTE}</p>
      </div>
    );
  }
  const actor = attributedActor(termination, events, ws.userId, ws.userName);
  const owner = deviceOwner(events, ws.userId, ws.userName);
  const tag = terminationTag(termination);
  const confidence = termination.termination_confidence ?? 'unknown';
  const fallback = termination.termination_kind ? terminationSentence(termination.termination_kind, termination.termination_kind === 'agent_device_bye' ? owner : actor) : null;
  return (
    <div className="wh-summary">
      <p className="wh-headline">
        <Icon icon={TAG_ICON[tag?.icon ?? 'unknown']} /> {terminationHeadline(termination, actor, owner)}
      </p>
      <p className="wh-confidence">
        <span className="wh-label">How sure:</span> <strong>{CONFIDENCE_LABEL[confidence]}</strong>
        <span className="meta"> {CONFIDENCE_HINT[confidence]}</span>
      </p>
      {termination.termination_explanation ? <p>{termination.termination_explanation}</p> : fallback ? <p>{fallback}</p> : null}
      <p className="meta wh-provenance">{PROVENANCE_NOTE}</p>
    </div>
  );
}

function EventList({ call, events, notes }: { call: Call; events: readonly TimelineEvent[]; notes: readonly CallNote[] | null }) {
  const ws = useWorkspace();
  const tz = ws.config.value?.tenant_timezone;
  const [expanded, setExpanded] = useState(false);
  const [active, setActive] = useState(0);
  const listRef = useRef<HTMLUListElement>(null);
  const win = timelineWindow(events.length, expanded);
  const shown = win.hidden ? [...events.slice(0, win.head), ...events.slice(win.tailFrom)] : events;
  const first = Date.parse(events[0]?.at ?? '');
  const focusIndex = Math.min(active, shown.length - 1);

  const onKeyDown = (e: KeyboardEvent<HTMLUListElement>) => {
    const items = [...(listRef.current?.querySelectorAll<HTMLElement>('[data-tl-item]') ?? [])];
    const current = items.findIndex(item => item === e.target);
    if (current < 0) return;
    const next = moveFocus(e.key, current, items.length);
    if (next === null) return;
    e.preventDefault();
    setActive(next);
    items[next]?.focus();
  };

  const dropDetail = (event: TimelineEvent) => dropEventDetail(event, (call as { voicemail_drop?: { name?: unknown; played_ms?: unknown } }).voicemail_drop);
  const item = (event: TimelineEvent, index: number) => {
    const at = Date.parse(event.at);
    const actor = actorFor(event.actor_user_id, ws.userId, ws.userName);
    const note = noteAction(event);
    return (
      <li key={event.id} className={`tl-item${event.terminal ? ' is-terminal' : ''}${note ? ' is-note' : ''}`} data-tl-item="" tabIndex={index === focusIndex ? 0 : -1} onFocus={() => setActive(index)}>
        <span className="tl-when">
          <time dateTime={event.at}>{clockTime(event.at, tz)}</time>
          {Number.isFinite(at) && Number.isFinite(first) ? <span className="tl-offset meta"> +{formatDuration(at - first)}</span> : null}
        </span>
        {note ? (
          <span className="tl-source source-note"><Icon icon={NotebookPen} /> Note</span>
        ) : (
          <span className={`tl-source source-${event.source}`}>
            <Icon icon={SOURCE_ICON[event.source]} /> {SOURCE_LABEL[event.source]}
          </span>
        )}
        <span className="tl-text">
          {eventSentence(event, actor)}
          {event.terminal ? <span className="tl-flag"> This ended the call.</span> : null}
          {event.confidence !== 'observed' ? <span className="tl-conf meta"> ({CONFIDENCE_LABEL[event.confidence]})</span> : null}
          {note ? <NoteQuote action={note} noteId={event.note_id ?? null} notes={notes} /> : null}
          {dropDetail(event) ? <span className="tl-drop meta"> {dropDetail(event)}</span> : null}
        </span>
        <details className="diagnostics tl-details">
          <summary>Technical details</summary>
          <dl>
            <dt>Event</dt>
            <dd className="mono">{event.kind}</dd>
            {event.cause_code ? <><dt>Cause code</dt><dd className="mono">{event.cause_code}</dd></> : null}
            {note && event.note_id ? <><dt>Note</dt><dd className="mono">{event.note_id}</dd></> : null}
            {event.cause_detail && !note ? <><dt>Cause</dt><dd>{event.cause_detail}</dd></> : null}
            <dt>Sequence</dt>
            <dd className="mono">{event.sequence}</dd>
            {event.leg_id ? <><dt>Leg</dt><dd className="mono">{legName(call, event, ws.userId, ws.userName)}</dd></> : null}
          </dl>
        </details>
      </li>
    );
  };

  return (
    <>
      <p className="meta" id="wh-events-hint">Use the arrow keys to move between events. Times are in the workspace time zone.</p>
      <ul ref={listRef} className="tl-list" role="list" aria-labelledby="wh-events" aria-describedby="wh-events-hint" onKeyDown={onKeyDown}>
        {shown.slice(0, win.head).map((e, i) => item(e, i))}
        {win.hidden ? (
          <li className="tl-gap">
            <button type="button" className="btn btn-small" onClick={() => setExpanded(true)}>
              Show {win.hidden} more event{win.hidden === 1 ? '' : 's'}
            </button>
          </li>
        ) : null}
        {win.hidden ? shown.slice(win.head).map((e, i) => item(e, win.head + i)) : null}
      </ul>
    </>
  );
}

/** A note event quotes its note when the page has it, and links to it in the Notes section. */
function NoteQuote({ action, noteId, notes }: { action: NoteAction; noteId: string | null; notes: readonly CallNote[] | null }) {
  if (!noteId || !notes || action === 'deleted') return null;
  const note = notes.find(n => n.id.toLowerCase() === noteId);
  if (!note) return <span className="meta tl-note-quote">This note has since been deleted.</span>;
  const body = note.body.length > 160 ? `${note.body.slice(0, 159).trimEnd()}…` : note.body;
  const show = (e: MouseEvent<HTMLAnchorElement>) => {
    const target = document.getElementById(`note-${note.id}`);
    if (!target) return;
    e.preventDefault();
    target.scrollIntoView({ block: 'center' });
    target.focus({ preventScroll: true });
  };
  return (
    <span className="tl-note-quote">
      <BlocklistInlineStyles />
      <q>{body}</q>{' '}
      <a href={`#note-${note.id}`} onClick={show}>Go to note</a>
    </span>
  );
}

function legName(call: Call, event: Pick<TimelineEvent, 'participant_id' | 'leg_id'>, selfId: string, name: (id: string | null | undefined) => string | null): string {
  const participant = event.participant_id ? call.participants.find(p => p.id === event.participant_id) : undefined;
  if (participant) {
    if (participant.kind === 'external') return 'Outside party';
    if (participant.user_id === selfId) return 'You';
    return name(participant.user_id) ?? 'Teammate';
  }
  return event.leg_id ? 'Call leg' : 'Whole call';
}

function MediaHealth({ call, legs }: { call: Call; legs: readonly LegHealth[] }) {
  const ws = useWorkspace();
  const tz = ws.config.value?.tenant_timezone;
  return (
    <>
      <h4 className="subsection-title">Connection quality</h4>
      {legs.length === 0 ? (
        <p className="field-hint">No connection measurements were recorded for this call.</p>
      ) : (
        <>
          <p className="meta">“Reported by the app” figures were sent by a device. “Observed by the server” figures were measured by the phone system.</p>
          <ul className="health-list" aria-label="Connection quality for each leg">
            {legs.map((leg, i) => {
              const states = [leg.iceState ? `network path ${stateWord(leg.iceState)}` : null, leg.dtlsState ? `encryption ${stateWord(leg.dtlsState)}` : null, leg.connectionState ? `connection ${stateWord(leg.connectionState)}` : null].filter(Boolean);
              return (
                <li key={leg.key} className="health-leg">
                  <p className="health-head">
                    <strong>{legName(call, { participant_id: leg.participantId, leg_id: leg.legId }, ws.userId, ws.userName)}{legs.length > 1 && !leg.participantId ? ` ${i + 1}` : ''}</strong>
                    <span className={`tl-source source-${leg.provenance === 'app' ? 'client_report' : 'pbx'}`}>
                      <Icon icon={leg.provenance === 'app' ? Laptop : ServerCog} /> {HEALTH_LABEL[leg.provenance]}
                    </span>
                  </p>
                  <dl className="health-facts">
                    <div><dt>Packet loss</dt><dd className="num">{leg.lossPercent === null ? 'Not measured' : formatLoss(leg.lossPercent)}</dd></div>
                    <div><dt>Jitter (worst)</dt><dd className="num">{leg.jitterMs === null ? 'Not measured' : formatMs(leg.jitterMs)}</dd></div>
                    <div><dt>Round trip (worst)</dt><dd className="num">{leg.rttMs === null ? 'Not measured' : formatMs(leg.rttMs)}</dd></div>
                    <div><dt>Reconnects</dt><dd className="num">{leg.reconnects === null ? 'Not measured' : leg.reconnects}</dd></div>
                  </dl>
                  {states.length ? <p className="meta">Last state: {states.join(', ')}.</p> : null}
                  <p className="meta">From {leg.samples} measurement{leg.samples === 1 ? '' : 's'}, last at <time dateTime={leg.lastAt}>{clockTime(leg.lastAt, tz)}</time>.</p>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </>
  );
}

/** Compact attribution for the calls list, from the Call's own fields. Renders nothing when unattributed. */
export function TerminationLabel({ call }: { call: Call }) {
  const tag = terminationTag(terminationOf(call));
  if (!tag) return null;
  const qualifier = tag.confidence && tag.confidence !== 'observed' ? CONFIDENCE_LABEL[tag.confidence] : null;
  return (
    <span className="row-badge row-ended" title={qualifier ? `${tag.text} (${qualifier})` : tag.text}>
      <Icon icon={TAG_ICON[tag.icon]} /> {tag.text}
      {qualifier ? <span className="visually-hidden"> ({qualifier})</span> : null}
    </span>
  );
}
